import React from "react";
import { Link } from "react-router-dom";
import { SectionHeader } from "@/components/shared/Common";
import { Button } from "@/components/ui/button";
import { Sparkles, ShieldCheck, HandCoins, Users2, Trophy, Lock, ChevronRight, HeartHandshake } from "lucide-react";
import ReadMore from "@/components/shared/ReadMore";

const benefits = [
  { icon: HandCoins, title: "You set the rate", desc: "You negotiate directly with the company. SecuSource never dictates your day-rate or hourly rate." },
  { icon: ShieldCheck, title: "Low commission per deployment", desc: "Only €1/hr per worker or 3% of the booking — whichever is lower. Paid by the company, not you." },
  { icon: HeartHandshake, title: "Built by the industry", desc: "Made by security professionals and CPOs, for security professionals and CPOs. We understand the work because we do the work." },
  { icon: Trophy, title: "Endorsements that count", desc: "Client endorsements strengthen your search ranking and reliability score across the whole industry." },
  { icon: Users2, title: "Team opportunities", desc: "Join or lead a cluster team for larger, specialist and multi-day operations." },
  { icon: Lock, title: "Privacy protected", desc: "Anonymous profiles keep your name, phone and email private until you accept a confirmed booking." },
];

export default function ForProfessionals() {
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
      {/* Free to Join banner */}
      <div className="inline-flex items-center gap-2 bg-gold-50 text-gold-600 border border-gold-200 px-3 py-1 rounded-full text-xs font-outfit font-bold uppercase tracking-widest mb-4">
        <ShieldCheck className="w-3.5 h-3.5" /> €0 to join · No membership fee · Ever
      </div>

      <SectionHeader
        eyebrow="For Security Officers"
        title="Free to join. ERO-minimum rates or above. We deliver value to your work."
      />
      <ReadMore
        className="max-w-3xl -mt-4"
        preview="Built by security professionals and CPOs — for security professionals and CPOs."
        more="It's free to sign up. You always negotiate your own rate. The company pays the platform commission, not you — and it's the lowest per-deployment fee in the sector."
        buttonMoreLabel="How it works"
        testId="for-pro-intro-readmore"
      />

      {/* Value pillars */}
      <div className="mt-4 grid md:grid-cols-3 gap-4">
        <div className="bg-navy-900 text-white rounded-xl p-6 shadow-sm" data-testid="value-free">
          <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold mb-2">Free to Join</div>
          <div className="text-3xl font-outfit font-bold mb-1 text-white">€0</div>
          <p className="text-slate-300 text-sm font-manrope">No sign-up cost. No monthly fee. No hidden charges to professionals — ever.</p>
        </div>
        <div className="card-flat p-6 border-l-4 border-l-brand" data-testid="value-rate">
          <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold mb-2">Your Rate</div>
          <div className="text-3xl font-outfit font-bold text-white mb-1">ERO or above</div>
          <p className="text-white/70 text-sm font-manrope">Statutory Employment Regulation Order rates are the floor. Negotiate up from there directly with the client.</p>
        </div>
        <div className="card-flat p-6 border-l-4 border-l-gold-500" data-testid="value-industry">
          <div className="text-xs uppercase tracking-widest text-gold-500 font-outfit font-bold mb-2">Industry-Built</div>
          <div className="text-3xl font-outfit font-bold text-white mb-1">By us. For us.</div>
          <p className="text-white/70 text-sm font-manrope">Made by security and CPO operators. We are improving the industry, together — one deployment at a time.</p>
        </div>
      </div>

      {/* Fee explanation */}
      <div className="mt-10 card-flat p-6 grid md:grid-cols-2 gap-6 items-center">
        <div>
          <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold mb-2">How the commission works</div>
          <h3 className="text-2xl font-outfit font-bold text-white mb-3">The client pays. You take home your full negotiated rate.</h3>
          <ReadMore
            className="text-sm"
            preview="SecuSource charges the company a small commission on each deployment."
            more="Either €1 per hour per worker or 3% of the booking value, whichever is lower. This transparent, low fee is what lets you keep your rate strong. You are never billed by SecuSource."
            buttonMoreLabel="See the math"
            testId="for-pro-commission-readmore"
          />
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="bg-[#0E0E0E] rounded-md p-3">
              <div className="text-xs uppercase text-white/60 font-outfit tracking-widest">Your take-home</div>
              <div className="text-2xl font-outfit font-bold text-gold-500">100%</div>
            </div>
            <div className="bg-[#0E0E0E] rounded-md p-3">
              <div className="text-xs uppercase text-white/60 font-outfit tracking-widest">Platform cost to you</div>
              <div className="text-2xl font-outfit font-bold text-white">€0</div>
            </div>
          </div>
        </div>
        <div className="bg-navy-900 text-white rounded-md p-6">
          <div className="text-xs uppercase tracking-widest text-white/50 font-outfit font-bold mb-2">Example — 12hr night shift @ €25/hr</div>
          <div className="space-y-2 font-manrope text-sm">
            <div className="flex justify-between"><span className="text-slate-300">Your rate</span><strong>€25.00/hr</strong></div>
            <div className="flex justify-between"><span className="text-slate-300">Hours worked</span><strong>12h</strong></div>
            <div className="flex justify-between border-t border-white/10 pt-2"><span className="text-slate-300">You take home</span><strong className="text-gold-400">€300.00</strong></div>
            <div className="flex justify-between text-xs text-white/50"><span>Client pays platform</span><span>€9.00 (3% — lower)</span></div>
            <div className="flex justify-between text-xs text-white/50"><span>SecuSource charges you</span><span className="text-gold-400">€0.00</span></div>
          </div>
        </div>
      </div>

      {/* Benefits grid */}
      <div className="mt-10">
        <SectionHeader eyebrow="Why join" title="Real value for your work." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {benefits.map((b) => (
            <details key={b.title} className="card-flat p-5 group cursor-pointer">
              <summary className="list-none">
                <div className="w-10 h-10 rounded-md bg-brand-light text-brand flex items-center justify-center mb-3">
                  <b.icon className="w-5 h-5" />
                </div>
                <div className="font-outfit font-semibold text-white mb-1 flex items-center justify-between">{b.title} <ChevronRight className="w-4 h-4 group-open:rotate-90 transition-transform text-brand" /></div>
              </summary>
              <div className="text-sm text-white/70 font-manrope leading-relaxed mt-2">{b.desc}</div>
            </details>
          ))}
        </div>
      </div>

      {/* Manifesto */}
      <div className="mt-12 rounded-xl p-8 bg-navy-900 text-white text-center shadow-sm">
        <Sparkles className="w-6 h-6 text-brand mx-auto mb-3" />
        <h3 className="text-2xl font-outfit font-bold mb-2 text-white">Security operators — still working the job — who built this for our own industry.</h3>
        <ReadMore
          className="max-w-2xl mx-auto !text-slate-300 text-center"
          preview="Not a marketplace built by outsiders. Not a tech company that discovered security."
          more="We're operators who still walk the door, the site and the principal. We built SecuSource low-cost on purpose — to improve how our industry is sourced, verified and deployed, with fair economics that respect your work."
          buttonMoreLabel="Read the manifesto"
          testId="for-pro-manifesto-readmore"
        />
      </div>

      {/* CTAs */}
      <div className="mt-8 flex flex-col sm:flex-row gap-3">
        <Button asChild size="lg" className="bg-brand hover:bg-brand-hover text-white px-6 py-6" data-testid="for-pro-join-btn">
          <Link to="/register/staff">Join SecuSource — Free <ChevronRight className="w-4 h-4 ml-1" /></Link>
        </Button>
        <Button asChild size="lg" variant="outline" className="border-brand text-brand hover:bg-brand-light px-6 py-6">
          <Link to="/pricing">See commission details</Link>
        </Button>
      </div>
    </div>
  );
}
